import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import type { FormEvent } from 'react'
import { ApiError, createUser, fetchUsers, patchUser } from '@/api/client'
import type { UserRole } from '@/api/types'
import { isAdminUser, isFinanceUser } from '@/api/types'
import { useAuth } from '@/auth/useAuth'
import { EmptyState } from '@/components/EmptyState'
import { LoadingState } from '@/components/LoadingState'
import { RoleBadge } from '@/components/RoleBadge'
import { Users } from 'lucide-react'
import { AdminSection } from '@/features/admin/AdminSection'

/** Roles accepted by the API create/patch enums. */
const FINANCE_ASSIGNABLE_ROLES: UserRole[] = ['owner', 'finance', 'viewer']
const ADMIN_ASSIGNABLE_ROLES: UserRole[] = ['owner', 'finance', 'admin', 'viewer']

function randomBreakGlassPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%'
  const bytes = new Uint8Array(20)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('')
}

export function UsersAdminPage() {
  const { user: me } = useAuth()
  const canCreate = isFinanceUser(me)
  const assignableRoles = isAdminUser(me) ? ADMIN_ASSIGNABLE_ROLES : FINANCE_ASSIGNABLE_ROLES
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<UserRole>('owner')
  const [password, setPassword] = useState(() => randomBreakGlassPassword())

  const usersQuery = useQuery({
    queryKey: ['users', 'admin'],
    queryFn: () => fetchUsers(0, 200),
  })

  const createMut = useMutation({
    mutationFn: createUser,
    onSuccess: async (created) => {
      setError(null)
      setSuccess(`Added ${created.email} as ${created.role}. They can sign in with Microsoft.`)
      setEmail('')
      setFullName('')
      setRole('owner')
      setPassword(randomBreakGlassPassword())
      await qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err: unknown) => {
      setSuccess(null)
      setError(err instanceof ApiError ? err.message : 'Failed to add user')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: UserRole; is_active?: boolean } }) =>
      patchUser(id, body),
    onSuccess: async () => {
      setError(null)
      setSuccess(null)
      await qc.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err: unknown) => {
      setSuccess(null)
      setError(err instanceof ApiError ? err.message : 'Failed to update user')
    },
  })

  const users = usersQuery.data?.items ?? []

  function onCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSuccess(null)
    const trimmedEmail = email.trim().toLowerCase()
    const trimmedName = fullName.trim()
    if (!trimmedEmail || !trimmedName || password.length < 12) return
    createMut.mutate({
      email: trimmedEmail,
      full_name: trimmedName,
      password,
      role,
    })
  }

  return (
    <AdminSection
      title="Access list"
      tone="emerald"
      icon={Users}
      lede="Who may sign in to ARIES and with which role. Production users authenticate with their Veersa Tech Microsoft account."
    >
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="alert alert--success" role="status">
          {success}
        </p>
      )}

      {canCreate ? (
        <form className="admin-form" onSubmit={onCreate}>
          <h3 className="admin-form__title">Add user</h3>
          <p className="field__hint" style={{ marginTop: 0 }}>
            Email must match their Microsoft account (typically @veersatech.com). A random
            break-glass password is stored for the API; day-to-day prod sign-in uses Entra SSO.
          </p>
          <div className="admin-form__row">
            <label className="field">
              <span className="field__label">Email</span>
              <input
                className="field__input"
                type="email"
                name="email"
                autoComplete="off"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@veersatech.com"
              />
            </label>
            <label className="field">
              <span className="field__label">Full name</span>
              <input
                className="field__input"
                type="text"
                name="full_name"
                autoComplete="off"
                required
                maxLength={200}
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </label>
            <label className="field">
              <span className="field__label">Role</span>
              <select
                className="field__input"
                value={role}
                onChange={(event) => setRole(event.target.value as UserRole)}
                aria-label="Role for new user"
              >
                {assignableRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="admin-form__row">
            <label className="field">
              <span className="field__label">Break-glass password</span>
              <input
                className="field__input"
                type="text"
                name="password"
                autoComplete="new-password"
                required
                minLength={12}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setPassword(randomBreakGlassPassword())}
            >
              Regenerate
            </button>
            <button type="submit" className="button button--primary" disabled={createMut.isPending}>
              {createMut.isPending ? 'Adding…' : 'Add user'}
            </button>
          </div>
        </form>
      ) : (
        <p className="alert alert--info" role="note">
          Only system admins can add users. You can still change role or deactivate existing
          accounts below.
        </p>
      )}

      {usersQuery.isLoading && (
        <LoadingState label="Loading users…" variant="skeleton-table" rows={8} columns={5} />
      )}

      {!usersQuery.isLoading && users.length === 0 && <EmptyState title="No users found." />}

      {users.length > 0 && (
        <div className="table-wrap">
          <table className="table table--dense">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Active</th>
                <th scope="col"> </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  assignableRoles={assignableRoles}
                  busy={patchMut.isPending}
                  onPatch={(body) => patchMut.mutate({ id: user.id, body })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminSection>
  )
}

function UserRow({
  user,
  assignableRoles,
  busy,
  onPatch,
}: {
  user: {
    id: string
    full_name: string
    email: string
    role: UserRole
    is_active: boolean
  }
  assignableRoles: UserRole[]
  busy: boolean
  onPatch: (body: { role?: UserRole; is_active?: boolean }) => void
}) {
  const [role, setRole] = useState(user.role)
  const [active, setActive] = useState(user.is_active)
  const dirty = role !== user.role || active !== user.is_active
  const roleOptions = assignableRoles.includes(user.role)
    ? assignableRoles
    : [user.role, ...assignableRoles]

  return (
    <tr>
      <td>
        <div className="admin-user-cell">
          <span className="admin-user-cell__name">{user.full_name}</span>
          <RoleBadge role={user.role} />
        </div>
      </td>
      <td>{user.email}</td>
      <td>
        <select
          className="field__input field__input--compact"
          value={role}
          onChange={(event) => setRole(event.target.value as UserRole)}
          aria-label={`Role for ${user.email}`}
        >
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </td>
      <td>
        <label className="field field--inline">
          <input
            type="checkbox"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
          <span className={active ? 'badge badge--status-on' : 'badge badge--status-off'}>
            {active ? 'Active' : 'Off'}
          </span>
        </label>
      </td>
      <td>
        <button
          type="button"
          className="button button--ghost"
          disabled={busy || !dirty}
          onClick={() => onPatch({ role, is_active: active })}
        >
          Save
        </button>
      </td>
    </tr>
  )
}
